import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ArrowRight, ArrowLeft, Check, PhoneCall, FileText, Calendar, Edit3 } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const createLead = async (values: any) => {
    const leadData = {
        params: {
            name: values.fullName,
            phone: values.contactNumber || values.phone,
            opportunity: "Healthcare Campaign - Interactive Funnel",
            salesperson_id: 17,
            company_id: 2,
            email_from: values.email || "",
            contact_name: values.fullName,
            city: values.location || values.city || "",
            description: `
Owner: ${values.owner || values.isOwner || "N/A"}
Speciality: ${values.speciality || "N/A"}
Website: ${values.websiteLink || "N/A"}
Current Revenue: ${values.revenue || values.monthlyRevenue || "N/A"}
Target Revenue: ${values.target || values.targetGoal || "N/A"}
Marketing Budget: ${values.marketingBudget || values.investBudget || "N/A"}
Appointments: ${values.appointments || "N/A"}
Average Ticket Size: ${values.ticket || values.ticketSize || "N/A"}
Preferred Connection Time: ${values.connectTime || values.connectionTime || "N/A"}
Submission Time: ${new Date().toLocaleString()}
      `.trim(),
        },
    };

    const response = await axios.post(
        "https://mysamplewebsite.in/api/crm_leads/create",
        leadData,
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    if (response.data.status !== "success") {
        throw new Error(response.data.message || "Failed to create lead");
    }

    return response.data;
};



const questions = [
    { id: "intro", type: "intro" },
    {
        id: "isOwner",
        title: "1. Are you the owner? ",
        type: "radio",
        options: ["Yes", "No"],
    },
    {
        id: "appointments",
        title: "2. How many appointments can your clinic handle in a month? ",
        type: "radio",
        options: ["30 - 40", "40 - 50", "60 - 70", "70 above"],
    },
    {
        id: "ticketSize",
        title: "3. What is your average ticket size/treatment cost? ",
        type: "radio",
        options: [
            "₹0 - 3000 (Please do not contact us we cannot help you)",
            "₹3000 - 10,000",
            "₹10,000 - 20,000",
            "₹20,000 - 40,000",
            "₹40,000 and above",
        ],
    },
    {
        id: "monthlyRevenue",
        title: "4. What is your current monthly revenue? ",
        type: "radio",
        options: [
            "₹0 - 1.5 Lacs",
            "₹2 Lacs - 5 Lacs",
            "₹5 Lacs - 10 Lacs",
            "₹10 Lacs - 30 Lacs",
            "₹30 Lacs - 1 Cr",
            "₹1Cr Above",
        ],
    },
    {
        id: "targetGoal",
        title: "5. What is your target monthly goal working with us? ",
        type: "radio",
        options: [
            "₹10 Lacs - 30 Lacs",
            "₹30 Lacs - 1 Cr",
            "₹1Cr - 3Cr",
            "₹3Cr - 7Cr",
            "₹10Cr+",
        ],
    },
    {
        id: "investBudget",
        title: "6. How much are you looking to invest in advertisements for acquiring new customers? ",
        type: "radio",
        options: [
            "₹0 - 50,000 (Hey you need to allocate at least  ₹80K - 1 Lac budget & we will show you our wonders, guaranteed)",
            "₹50K - 1 Lac",
            "₹1Lac - 1.5 Lacs",
            "₹1.5Lacs - 2 Lacs",
            "₹2 Lacs - 5 Lacs",
            "₹5 Lacs - 10 Lacs",
            "₹10 Lacs - 30 Lacs",
            "₹30 Lacs - 1 Cr",
            "₹1Cr Above",
        ],
    },
    { id: "final", type: "final" },
];

const validationSchemas = [
    Yup.object({
        fullName: Yup.string().required("Full name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        phone: Yup.string()
            .matches(/^[0-9]{10,}$/, "Phone number must contain at least 10 digits.")
            .required("Phone number is required"),
        city: Yup.string().required("City is required"),
    }),
    Yup.object({ isOwner: Yup.string().required("Please select an option") }),
    Yup.object({ appointments: Yup.string().required("Please select an option") }),
    Yup.object({ ticketSize: Yup.string().required("Please select an option") }),
    Yup.object({ monthlyRevenue: Yup.string().required("Please select an option") }),
    Yup.object({ targetGoal: Yup.string().required("Please select an option") }),
    Yup.object({ investBudget: Yup.string().required("Please select an option") }),
    Yup.object({
        speciality: Yup.string().required("Speciality is required"),
        websiteLink: Yup.string(),
        connectionTime: Yup.string().required("Connection time is required"),
        finalMonthlyRevenue: Yup.string(),
        finalTargetRevenue: Yup.string(),
    }),
];

const InteractiveHealthcare = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (window.fbq) {
            window.fbq('init', '1307490394763706');
            window.fbq('track', 'PageView');
        }
    }, []);
    const [isAnimating, setIsAnimating] = useState(false);

    // Handle Enter key for proceeding
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                formik.handleSubmit();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [step]);

    const currentSchema = validationSchemas[step];

    const formik = useFormik({
        initialValues: {
            fullName: "",
            email: "",
            phone: "",
            city: "",
            isOwner: "",
            appointments: "",
            ticketSize: "",
            monthlyRevenue: "",
            targetGoal: "",
            investBudget: "",
            speciality: "",
            websiteLink: "",
            connectionTime: "",
            finalMonthlyRevenue: "",
            finalTargetRevenue: "",
        },
        validationSchema: currentSchema,
        validateOnChange: false,
        onSubmit: async (values, actions) => {
            if (step === questions.length - 1) {
                // Final Submission
                try {
                    toast.loading("Submitting...", { id: "submit" });

                    await createLead(values);

                    toast.success("Submitted successfully!", { id: "submit" });

                    navigate("/thankyou-healthcare");

                } catch (error: any) {
                    toast.error(
                        error.response?.data?.message ||
                        error.message ||
                        "Submission failed",
                        { id: "submit" }
                    );
                } finally {
                    actions.setSubmitting(false);
                }
            }
            else {
                // Next Step
                setIsAnimating(true);
                setTimeout(() => {
                    setStep((prev) => prev + 1);
                    setIsAnimating(false);
                }, 300);
                actions.setTouched({});
                actions.setSubmitting(false);
            }
        },
    });

    const nextStep = () => {
        formik.handleSubmit();
    };

    const prevStep = () => {
        if (step > 0) {
            setIsAnimating(true);
            setTimeout(() => {
                setStep((prev) => prev - 1);
                setIsAnimating(false);
            }, 300);
        }
    };

    const handleRadioSelect = (field: string, value: string) => {
        formik.setFieldValue(field, value);
        // Auto-advance after short delay
        setTimeout(() => {
            nextStep();
        }, 400);
    };

    const getProgress = () => {
        return ((step + 1) / questions.length) * 100;
    };

    const renderIntro = () => {
        return (
            <div className="flex flex-col md:flex-row w-full max-w-5xl bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100">
                <div className="md:w-5/12 bg-[#3E88FF] text-white p-10 flex flex-col justify-center">
                    <h2 className="text-3xl font-bold mb-6 leading-tight">Meet India's Only "  Acquisition" Experts</h2>
                    <ul className="space-y-4 text-lg text-blue-50">
                        <li className="flex items-start">
                            <PhoneCall className="mr-3 mt-1 h-5 w-5 shrink-0" /> We are the only marketing agency that gives tele sales support
                        </li>
                        <li className="flex items-start">
                            <FileText className="mr-3 mt-1 h-5 w-5 shrink-0" /> Our reports talk strictly about appointments &   walk-ins
                        </li>
                        <li className="flex items-start">
                            <Calendar className="mr-3 mt-1 h-5 w-5 shrink-0" /> We will tell you how we do it in our 20 mins discovery call
                        </li>
                        <li className="flex items-start">
                            <Edit3 className="mr-3 mt-1 h-5 w-5 shrink-0" /> Fill in your details correctly as this is crucial for our discussion
                        </li>
                    </ul>
                </div>
                <div className="md:w-7/12 p-10 flex flex-col justify-center bg-gray-50">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Let's start with the basics</h3>
                    <div className="space-y-5">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1 text-left">Full name *</label>
                            <input
                                type="text"
                                name="fullName"
                                placeholder="Enter your answer"
                                className="w-full border-b-2 border-gray-300 bg-transparent px-2 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors"
                                onChange={formik.handleChange}
                                value={formik.values.fullName}
                            />
                            {formik.errors.fullName && <div className="text-red-500 text-sm mt-1">{formik.errors.fullName}</div>}
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1 text-left">Email *</label>
                            <input
                                type="email"
                                name="email"
                                placeholder="Enter your answer"
                                className="w-full border-b-2 border-gray-300 bg-transparent px-2 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors"
                                onChange={formik.handleChange}
                                value={formik.values.email}
                            />
                            {formik.errors.email && <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>}
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1 text-left">Phone number *</label>
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Enter your answer"
                                className="w-full border-b-2 border-gray-300 bg-transparent px-2 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors"
                                onChange={formik.handleChange}
                                value={formik.values.phone}
                            />
                            {formik.errors.phone && <div className="text-red-500 text-sm mt-1">{formik.errors.phone}</div>}
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1 text-left">City *</label>
                            <input
                                type="text"
                                name="city"
                                placeholder="Enter your answer"
                                className="w-full border-b-2 border-gray-300 bg-transparent px-2 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors"
                                onChange={formik.handleChange}
                                value={formik.values.city}
                            />
                            {formik.errors.city && <div className="text-red-500 text-sm mt-1">{formik.errors.city}</div>}
                        </div>
                    </div>
                    <button
                        onClick={nextStep}
                        className="mt-8 bg-[#3E88FF] hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-lg shadow-md transition-all flex items-center justify-center self-start text-lg"
                    >
                        Start Now <ArrowRight className="ml-2 w-5 h-5" />
                    </button>
                </div>
            </div>
        );
    };

    const renderRadioQuestion = (q: any) => {
        return (
            <div className="w-full max-w-3xl flex flex-col justify-center">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 leading-tight">
                    {q.title}
                </h2>
                <div className="space-y-4">
                    {q.options.map((option: string, index: number) => {
                        const isSelected = formik.values[q.id as keyof typeof formik.values] === option;
                        const letter = String.fromCharCode(65 + index); // A, B, C...
                        return (
                            <div
                                key={option}
                                onClick={() => handleRadioSelect(q.id, option)}
                                className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 text-lg md:text-xl transform hover:-translate-y-1 hover:shadow-md
                  ${isSelected ? "border-[#3E88FF] bg-blue-50" : "border-gray-200 bg-white hover:border-[#3E88FF]"}`}
                            >
                                <div
                                    className={`flex items-center justify-center w-8 h-8 rounded border mr-4 font-bold text-sm
                    ${isSelected ? "bg-[#3E88FF] text-white border-[#3E88FF] border-none" : "border-gray-300 text-gray-600"}`}
                                >
                                    {letter}
                                </div>
                                <span className="flex-1 font-medium text-gray-700">{option}</span>
                            </div>
                        );
                    })}
                </div>
                {formik.errors[q.id as keyof typeof formik.errors] && (
                    <div className="text-red-500 text-sm mt-4 p-3 bg-red-50 rounded text-center">{formik.errors[q.id as keyof typeof formik.errors] as string}</div>
                )}
                <div className="flex space-x-4 mt-10">
                    <button
                        onClick={nextStep}
                        className="bg-[#3E88FF] hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-lg shadow-md transition-all flex items-center justify-center text-lg"
                    >
                        OK <Check className="ml-2 w-5 h-5" />
                    </button>
                </div>
                <p className="text-xs text-gray-400 mt-4 flex items-center">press <span className="font-bold mx-1">Enter ↵</span> to continue</p>
            </div>
        );
    };

    const renderFinal = () => {
        return (
            <div className="w-full max-w-4xl bg-white p-8 md:p-12 shadow-2xl rounded-2xl border border-gray-100">
                <h2 className="text-3xl font-bold text-gray-800 mb-8 border-b pb-4">Almost done! Just a few more details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">What is your speciality? *</label>
                        <input
                            type="text"
                            name="speciality"
                            placeholder="Enter your answer"
                            className="w-full border-b-2 border-gray-300 bg-gray-50 px-3 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors rounded-t-md"
                            onChange={formik.handleChange}
                            value={formik.values.speciality}
                        />
                        {formik.errors.speciality && <div className="text-red-500 text-sm mt-1">{formik.errors.speciality}</div>}
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Website Link?</label>
                        <input
                            type="text"
                            name="websiteLink"
                            placeholder="https://example.com"
                            className="w-full border-b-2 border-gray-300 bg-gray-50 px-3 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors rounded-t-md"
                            onChange={formik.handleChange}
                            value={formik.values.websiteLink}
                        />
                        {formik.errors.websiteLink && <div className="text-red-500 text-sm mt-1">{formik.errors.websiteLink}</div>}
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">When would you like to connect? Mention Date & Time *</label>
                        <input
                            type="text"
                            name="connectionTime"
                            placeholder="e.g. Tomorrow 10:00 AM"
                            className="w-full border-b-2 border-gray-300 bg-gray-50 px-3 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors rounded-t-md"
                            onChange={formik.handleChange}
                            value={formik.values.connectionTime}
                        />
                        {formik.errors.connectionTime && <div className="text-red-500 text-sm mt-1">{formik.errors.connectionTime}</div>}
                    </div>
                    {/* Duplicated questions at the end if client explicitly wants them */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">What is your business's current monthly revenue?</label>
                        <input
                            type="text"
                            name="finalMonthlyRevenue"
                            placeholder="Enter your answer"
                            className="w-full border-b-2 border-gray-300 bg-gray-50 px-3 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors rounded-t-md"
                            onChange={formik.handleChange}
                            value={formik.values.finalMonthlyRevenue}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">What is your target monthly revenue goal?</label>
                        <input
                            type="text"
                            name="finalTargetRevenue"
                            placeholder="Enter your answer"
                            className="w-full border-b-2 border-gray-300 bg-gray-50 px-3 py-3 text-lg focus:outline-none focus:border-[#3E88FF] transition-colors rounded-t-md"
                            onChange={formik.handleChange}
                            value={formik.values.finalTargetRevenue}
                        />
                    </div>
                </div>
                <button
                    onClick={nextStep}
                    className="mt-10 bg-[#3E88FF] hover:bg-blue-600 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-transform hover:scale-105 flex items-center justify-center w-full md:w-auto text-xl"
                >
                    Submit Form
                </button>
            </div>
        );
    };

    const renderCurrentStep = () => {
        const q = questions[step];
        if (q.type === "intro") return renderIntro();
        if (q.type === "radio") return renderRadioQuestion(q);
        if (q.type === "final") return renderFinal();
        return null;
    };

    return (
        <div className="min-h-screen bg-white font-poppins relative flex flex-col">
            <Toaster position="top-center" />

            {/* Header with progress */}
            <header className="fixed top-0 w-full z-10 bg-white/80 backdrop-blur-sm border-b border-gray-100 flex items-center h-16 px-6">
                <h1 className="font-anton text-2xl tracking-wide text-black flex items-baseline cursor-pointer" onClick={() => navigate("/")}>
                    DUMOSH<span className="h-1.5 w-1.5 bg-[#FF3E3E] rounded-full ml-0.5"></span>
                </h1>
                <div className="flex-1 mx-8 max-w-xl hidden sm:block">
                    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#3E88FF] transition-all duration-500 ease-out"
                            style={{ width: `${getProgress()}%` }}
                        ></div>
                    </div>
                </div>
                <div className="ml-auto text-gray-500 font-medium text-sm">
                    {step > 0 && <span className="mr-2">{step} / {questions.length - 1}</span>}
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col items-center justify-center px-4 pt-16 pb-24 overflow-y-auto w-full min-h-[500px]">
                <div
                    className={`w-full flex justify-center transition-all duration-500 transform
            ${isAnimating ? "opacity-0 translate-y-8" : "opacity-100 translate-y-0"}
          `}
                >
                    {renderCurrentStep()}
                </div>
            </main>

            {/* Footer Navigation */}
            {step > 0 && (
                <footer className="fixed bottom-0 w-full bg-white border-t border-gray-100 p-4 flex justify-end gap-3 px-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                    <button
                        onClick={prevStep}
                        className="flex items-center justify-center p-3 text-white bg-[#3E88FF] hover:bg-blue-600 rounded-lg transition-colors cursor-pointer"
                        title="Previous"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>

                    {questions[step].type !== "final" && formik.values[questions[step].id as keyof typeof formik.values] && (
                        <button
                            onClick={nextStep}
                            className="flex items-center justify-center px-5 py-3 text-white bg-[#3E88FF] hover:bg-blue-600 rounded-lg transition-colors cursor-pointer"
                            title="Next"
                        >
                            <ArrowRight className="w-5 h-5 ml-1" />
                        </button>
                    )}
                </footer>
            )}
        </div>
    );
};

export default InteractiveHealthcare;
